import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

const api =
    new API();


/*
|--------------------------------------------------------------------------
| DEFAULTS
|--------------------------------------------------------------------------
*/

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
};

const DEFAULT_SUMMARY = {
    suppliersPayable: 0,

    totalOutstanding: 0,

    overdueAmount: 0,

    notDue: 0,

    days0To30: 0,

    days31To60: 0,

    days61To90: 0,

    days90Plus: 0,
};

const DEFAULT_DETAILS = {
    supplier: null,

    totals: {
        totalBills: 0,
        overdueBills: 0,

        totalPurchase: 0,

        totalDebitNotes: 0,

        totalPaid: 0,

        totalOutstanding: 0,
    },

    ageing: {
        notDue: 0,

        days0To30: 0,

        days31To60: 0,

        days61To90: 0,

        days90Plus: 0,

        overdueAmount: 0,
    },

    bills: [],
};


/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback =
        "Something went wrong."
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseEnvelope = (
    response
) => {
    if (
        !response
    ) {
        return {};
    }

    /*
    |--------------------------------------------------------------------------
    | Axios-like response
    |--------------------------------------------------------------------------
    */

    if (
        response?.data &&
        typeof response.data ===
            "object" &&
        !Array.isArray(
            response.data
        ) &&
        (
            response?.status !==
                undefined ||
            response?.headers !==
                undefined ||
            response?.config !==
                undefined
        )
    ) {
        return response.data;
    }

    return response;
};


const getResponseData = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    return (
        envelope?.data ??
        envelope
    );
};


const getRecords = (
    response
) => {
    const data =
        getResponseData(
            response
        );

    if (
        Array.isArray(
            data
        )
    ) {
        return data;
    }

    if (
        Array.isArray(
            data?.records
        )
    ) {
        return data.records;
    }

    return [];
};


const getPagination = (
    response
) => {
    const data =
        getResponseData(
            response
        );

    const pagination =
        data?.pagination ||
        {};

    return {
        ...DEFAULT_PAGINATION,

        ...pagination,

        page:
            Number(
                pagination?.page
            ) || 1,

        limit:
            Number(
                pagination?.limit
            ) || 20,

        total:
            Number(
                pagination?.total
            ) || 0,

        totalPages:
            Math.max(
                Number(
                    pagination?.totalPages
                ) || 1,
                1
            ),

        hasNextPage:
            Boolean(
                pagination?.hasNextPage
            ),

        hasPreviousPage:
            Boolean(
                pagination?.hasPreviousPage
            ),
    };
};


const getSummary = (
    response
) => {
    const data =
        getResponseData(
            response
        );

    return (
        data?.summary ||
        {}
    );
};


/*
|--------------------------------------------------------------------------
| QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value ===
                    undefined ||
                value ===
                    null ||
                value ===
                    ""
            ) {
                return;
            }

            query.append(
                key,
                value
            );
        }
    );

    const value =
        query.toString();

    return value
        ? `?${value}`
        : "";
};


/*
|--------------------------------------------------------------------------
| FETCH SUPPLIER PAYABLE LIST
|--------------------------------------------------------------------------
*/

export const fetchSupplierPayables =
    createAsyncThunk(
        "supplierPayable/fetchSupplierPayables",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        search:
                            params.search,

                        status:
                            params.status,

                        asOfDate:
                            params.asOfDate,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierPayables()}${queryString}`
                    );

                return {
                    records:
                        getRecords(
                            response
                        ),

                    pagination:
                        getPagination(
                            response
                        ),

                    summary:
                        getSummary(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier payables."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH SUPPLIER PAYABLE DETAILS
|--------------------------------------------------------------------------
*/

export const fetchSupplierPayableDetails =
    createAsyncThunk(
        "supplierPayable/fetchSupplierPayableDetails",

        async (
            {
                supplierId,
                asOfDate,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const queryString =
                    buildQueryString({
                        asOfDate,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierPayableDetails(
                            supplierId
                        )}${queryString}`
                    );

                return (
                    getResponseData(
                        response
                    ) ||
                    null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier payable details."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH SUPPLIER OUTSTANDING BILLS
|--------------------------------------------------------------------------
*/

export const fetchSupplierPayableBills =
    createAsyncThunk(
        "supplierPayable/fetchSupplierPayableBills",

        async (
            {
                supplierId,
                asOfDate,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const queryString =
                    buildQueryString({
                        asOfDate,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierPayableBills(
                            supplierId
                        )}${queryString}`
                    );

                const data =
                    getResponseData(
                        response
                    ) || {};

                return {
                    records:
                        Array.isArray(
                            data?.records
                        )
                            ? data.records
                            : [],

                    summary:
                        data?.summary ||
                        {},
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier outstanding bills."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH AGEING SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchSupplierPayableAgeingSummary =
    createAsyncThunk(
        "supplierPayable/fetchSupplierPayableAgeingSummary",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        search:
                            params.search,

                        status:
                            params.status,

                        asOfDate:
                            params.asOfDate,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierPayableAgeingSummary()}${queryString}`
                    );

                return (
                    getResponseData(
                        response
                    ) ||
                    {}
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier ageing summary."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH SUPPLIER LEDGER BALANCE
|--------------------------------------------------------------------------
*/

export const fetchSupplierPayableLedgerBalance =
    createAsyncThunk(
        "supplierPayable/fetchSupplierPayableLedgerBalance",

        async (
            supplierId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    return rejectWithValue(
                        "Supplier ID is required."
                    );
                }

                const response =
                    await api.get(
                        config.getSupplierPayableLedgerBalance(
                            supplierId
                        )
                    );

                const data =
                    getResponseData(
                        response
                    ) || {};

                return {
                    supplierId:
                        data?.supplierId ||
                        supplierId,

                    balance:
                        Number(
                            data?.balance ||
                            0
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier ledger balance."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    /*
    |--------------------------------------------------------------------------
    | LIST
    |--------------------------------------------------------------------------
    */

    records: [],


    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    pagination: {
        ...DEFAULT_PAGINATION,
    },


    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    summary: {
        ...DEFAULT_SUMMARY,
    },


    /*
    |--------------------------------------------------------------------------
    | AGEING SUMMARY
    |--------------------------------------------------------------------------
    */

    ageingSummary: {
        ...DEFAULT_SUMMARY,

        asOfDate: null,
    },


    /*
    |--------------------------------------------------------------------------
    | DETAILS
    |--------------------------------------------------------------------------
    */

    selectedSupplierPayable: {
        ...DEFAULT_DETAILS,

        totals: {
            ...DEFAULT_DETAILS
                .totals,
        },

        ageing: {
            ...DEFAULT_DETAILS
                .ageing,
        },

        bills: [],
    },


    /*
    |--------------------------------------------------------------------------
    | OUTSTANDING BILLS
    |--------------------------------------------------------------------------
    */

    outstandingBills: [],

    outstandingBillsSummary: {
        totalBills: 0,

        overdueBills: 0,

        totalOutstanding: 0,

        overdueAmount: 0,
    },


    /*
    |--------------------------------------------------------------------------
    | LEDGER BALANCE
    |--------------------------------------------------------------------------
    */

    ledgerBalance: {
        supplierId: null,

        balance: 0,
    },


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    loading: {
        list: false,

        details: false,

        bills: false,

        ageingSummary: false,

        ledgerBalance: false,
    },


    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    error: {
        list: null,

        details: null,

        bills: null,

        ageingSummary: null,

        ledgerBalance: null,
    },
};


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const supplierPayableSlice =
    createSlice({
        name:
            "supplierPayable",

        initialState,

        reducers: {
            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED
            |--------------------------------------------------------------------------
            */

            clearSelectedSupplierPayable: (
                state
            ) => {
                state.selectedSupplierPayable =
                {
                    ...DEFAULT_DETAILS,

                    totals: {
                        ...DEFAULT_DETAILS
                            .totals,
                    },

                    ageing: {
                        ...DEFAULT_DETAILS
                            .ageing,
                    },

                    bills: [],
                };

                state.error.details =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR BILLS
            |--------------------------------------------------------------------------
            */

            clearSupplierPayableBills: (
                state
            ) => {
                state.outstandingBills =
                    [];

                state.outstandingBillsSummary =
                {
                    totalBills:
                        0,

                    overdueBills:
                        0,

                    totalOutstanding:
                        0,

                    overdueAmount:
                        0,
                };

                state.error.bills =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR LEDGER BALANCE
            |--------------------------------------------------------------------------
            */

            clearSupplierPayableLedgerBalance: (
                state
            ) => {
                state.ledgerBalance =
                {
                    supplierId:
                        null,

                    balance:
                        0,
                };

                state.error.ledgerBalance =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ERRORS
            |--------------------------------------------------------------------------
            */

            clearSupplierPayableErrors: (
                state
            ) => {
                state.error = {
                    list:
                        null,

                    details:
                        null,

                    bills:
                        null,

                    ageingSummary:
                        null,

                    ledgerBalance:
                        null,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET
            |--------------------------------------------------------------------------
            */

            resetSupplierPayableState:
                () => ({
                    ...initialState,

                    records:
                        [],

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },

                    summary: {
                        ...DEFAULT_SUMMARY,
                    },

                    ageingSummary: {
                        ...DEFAULT_SUMMARY,

                        asOfDate:
                            null,
                    },

                    selectedSupplierPayable:
                    {
                        ...DEFAULT_DETAILS,

                        totals: {
                            ...DEFAULT_DETAILS
                                .totals,
                        },

                        ageing: {
                            ...DEFAULT_DETAILS
                                .ageing,
                        },

                        bills:
                            [],
                    },

                    outstandingBills:
                        [],

                    outstandingBillsSummary:
                    {
                        totalBills:
                            0,

                        overdueBills:
                            0,

                        totalOutstanding:
                            0,

                        overdueAmount:
                            0,
                    },

                    ledgerBalance:
                    {
                        supplierId:
                            null,

                        balance:
                            0,
                    },

                    loading: {
                        ...initialState
                            .loading,
                    },

                    error: {
                        ...initialState
                            .error,
                    },
                }),
        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | LIST
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierPayables
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.list =
                            true;

                        state.error.list =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierPayables
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.records =
                            Array.isArray(
                                action
                                    .payload
                                    ?.records
                            )
                                ? action
                                    .payload
                                    .records
                                : [];

                        state.pagination = {
                            ...DEFAULT_PAGINATION,

                            ...(action
                                .payload
                                ?.pagination ||
                                {}),
                        };

                        state.summary = {
                            ...DEFAULT_SUMMARY,

                            ...(action
                                .payload
                                ?.summary ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchSupplierPayables
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.error.list =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier payables.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | DETAILS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierPayableDetails
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.details =
                            true;

                        state.error.details =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierPayableDetails
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        const data =
                            action.payload ||
                            {};

                        state.selectedSupplierPayable =
                        {
                            supplier:
                                data
                                    ?.supplier ||
                                null,

                            totals: {
                                ...DEFAULT_DETAILS
                                    .totals,

                                ...(data
                                    ?.totals ||
                                    {}),
                            },

                            ageing: {
                                ...DEFAULT_DETAILS
                                    .ageing,

                                ...(data
                                    ?.ageing ||
                                    {}),
                            },

                            bills:
                                Array.isArray(
                                    data
                                        ?.bills
                                )
                                    ? data
                                        .bills
                                    : [],
                        };
                    }
                )

                .addCase(
                    fetchSupplierPayableDetails
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier payable details.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | BILLS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierPayableBills
                        .pending,
                    (
                        state
                    ) => {
                        state.loading.bills =
                            true;

                        state.error.bills =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierPayableBills
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.bills =
                            false;

                        state.outstandingBills =
                            Array.isArray(
                                action
                                    .payload
                                    ?.records
                            )
                                ? action
                                    .payload
                                    .records
                                : [];

                        state.outstandingBillsSummary =
                        {
                            totalBills:
                                0,

                            overdueBills:
                                0,

                            totalOutstanding:
                                0,

                            overdueAmount:
                                0,

                            ...(action
                                .payload
                                ?.summary ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchSupplierPayableBills
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.bills =
                            false;

                        state.outstandingBills =
                            [];

                        state.error.bills =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier outstanding bills.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | AGEING SUMMARY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierPayableAgeingSummary
                        .pending,
                    (
                        state
                    ) => {
                        state.loading
                            .ageingSummary =
                            true;

                        state.error
                            .ageingSummary =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierPayableAgeingSummary
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .ageingSummary =
                            false;

                        state.ageingSummary =
                        {
                            ...DEFAULT_SUMMARY,

                            ...(action
                                .payload ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchSupplierPayableAgeingSummary
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .ageingSummary =
                            false;

                        state.error
                            .ageingSummary =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier ageing summary.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | LEDGER BALANCE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierPayableLedgerBalance
                        .pending,
                    (
                        state
                    ) => {
                        state.loading
                            .ledgerBalance =
                            true;

                        state.error
                            .ledgerBalance =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierPayableLedgerBalance
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .ledgerBalance =
                            false;

                        state.ledgerBalance =
                        {
                            supplierId:
                                action
                                    .payload
                                    ?.supplierId ||
                                null,

                            balance:
                                Number(
                                    action
                                        .payload
                                        ?.balance ||
                                    0
                                ),
                        };
                    }
                )

                .addCase(
                    fetchSupplierPayableLedgerBalance
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading
                            .ledgerBalance =
                            false;

                        state.error
                            .ledgerBalance =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier ledger balance.";
                    }
                );
        },
    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearSelectedSupplierPayable,

    clearSupplierPayableBills,

    clearSupplierPayableLedgerBalance,

    clearSupplierPayableErrors,

    resetSupplierPayableState,
} =
    supplierPayableSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
|
| Root reducer:
|
| supplierPayable: supplierPayableReducer
|--------------------------------------------------------------------------
*/

export const supplierPayableStateSelector = (
    state
) =>
    state?.supplierPayable ||
    initialState;


export const supplierPayableListSelector = (
    state
) =>
    state?.supplierPayable
        ?.records ||
    [];


export const supplierPayablePaginationSelector = (
    state
) =>
    state?.supplierPayable
        ?.pagination ||
    initialState
        .pagination;


export const supplierPayableSummarySelector = (
    state
) =>
    state?.supplierPayable
        ?.summary ||
    initialState
        .summary;


export const supplierPayableAgeingSummarySelector = (
    state
) =>
    state?.supplierPayable
        ?.ageingSummary ||
    initialState
        .ageingSummary;


export const selectedSupplierPayableSelector = (
    state
) =>
    state?.supplierPayable
        ?.selectedSupplierPayable ||
    initialState
        .selectedSupplierPayable;


export const supplierPayableBillsSelector = (
    state
) =>
    state?.supplierPayable
        ?.outstandingBills ||
    [];


export const supplierPayableBillsSummarySelector = (
    state
) =>
    state?.supplierPayable
        ?.outstandingBillsSummary ||
    initialState
        .outstandingBillsSummary;


export const supplierPayableLedgerBalanceSelector = (
    state
) =>
    state?.supplierPayable
        ?.ledgerBalance ||
    initialState
        .ledgerBalance;


export const supplierPayableLoadingSelector = (
    state
) =>
    state?.supplierPayable
        ?.loading ||
    initialState
        .loading;


export const supplierPayableErrorSelector = (
    state
) =>
    state?.supplierPayable
        ?.error ||
    initialState
        .error;


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default supplierPayableSlice.reducer;